import { createRoot } from "react-dom/client";
import { TimelineGroupType } from "../timeline";
import TimelineItem from "../timeline-item";
import TimelineGroupItem from "../timeline-group-item";
import { TimelineOptions } from "vis-timeline";

export interface TimelineOptionsProps {
  handleHide?: (item: TimelineGroupType) => void;
  handleSave?: (item: TimelineGroupType) => void;
}

export const useTimelineOptions = ({
  handleHide,
  handleSave,
}: TimelineOptionsProps): TimelineOptions => {
  const customSort = (a: any, b: any) => {
    const contentA = a?.content?.toLowerCase();
    const contentB = b?.content?.toLowerCase();

    if (!contentA && !contentB) return 0;
    if (!contentA) return 1;
    if (!contentB) return -1;

    // Check for "Order" or "order" first
    if (contentA.startsWith("order")) return -1;
    if (contentB.startsWith("order")) return 1;

    // Helper function to check if string starts with a number
    const startsWithNumber = (str: string) => /^\d/.test(str);

    // If one starts with a number and the other doesn't
    if (startsWithNumber(contentA) && !startsWithNumber(contentB)) return 1;
    if (!startsWithNumber(contentA) && startsWithNumber(contentB)) return -1;

    // If both start with numbers or both start with letters
    if (startsWithNumber(contentA) && startsWithNumber(contentB)) {
      // Extract numbers and compare
      const numA = parseInt(contentA.match(/^\d+/)?.[0] || "0");
      const numB = parseInt(contentB.match(/^\d+/)?.[0] || "0");
      return numA - numB;
    }

    // Default alphabetical sorting
    return contentA.localeCompare(contentB);
  };

  return {
    orientation: "top",
    verticalScroll: true,
    showCurrentTime: false,
    // @ts-ignore
    template: function (item: any, element: HTMLElement) {
      let root = (element as any)._react_root;

      if (!root) {
        root = createRoot(element);
        (element as any)._react_root = root;
      }

      root.render(<TimelineItem view={item.view} color={item.color} />);

      return () => {
        root.unmount();
        delete (element as any)._react_root;
      };
    },
    // @ts-ignore
    groupTemplate: function (group: any, element: HTMLElement) {
      let root = (element as any)._react_root;

      if (!root) {
        root = createRoot(element);
        (element as any)._react_root = root;
      }

      root.render(
        <TimelineGroupItem
          content={group.content}
          color={group.color}
          canHide={group.canHide}
          canSave={group.canSave}
          isHidden={group.isHidden}
          isSaved={group.isSaved}
          onHide={() => handleHide?.(group) ?? undefined}
          onSave={() => handleSave?.(group) ?? undefined}
        />
      );

      return () => {
        root.unmount();
        delete (element as any)._react_root;
      };
    },
    groupOrder: customSort,

    zoomMin: 1000, // 1 second in milliseconds
    zoomMax: 1000 * 60 * 60 * 24, // 1 day
    maxMinorChars: 7,
    snap: function (date: Date, _scale: any, _step: any) {
      const resolution = 10; // ms
      return Math.round(date.getTime() / resolution) * resolution;
    },
    format: {
      minorLabels: { millisecond: "s.S[s]", second: "s[s]" },
      majorLabels: { second: "HH:mm:ss", millisecond: "HH:mm:ss" },
    },
    margin: { axis: 0, item: 0 },
    stack: false,
    horizontalScroll: true,
    zoomKey: "shiftKey",
    zoomFriction: 50,
    moveable: true,
  };
};
